'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useCursorEnabled } from './useCursorEnabled';

type Ripple = { x: number; y: number; startMs: number };

const DOT_LERP = 0.32;
const RING_LERP = 0.16;
const DOT_RADIUS = 3.5;
const RING_RADIUS_DEFAULT = 12;
const RING_RADIUS_HOVER = 24;
const RIPPLE_LIFE_MS = 1200;
const RIPPLE_MAX_RADIUS = 80;
const RIPPLE_MAX = 3;
const RIPPLE_MIN_DISTANCE = 44;
const RIPPLE_IDLE_MS = 150;

const HOVERABLE_SELECTOR =
  'a, button, [role="button"], [data-magnetic], .featured-card, .ai-card, .service-card, .post-card, .small-card, .cap-tile';
const TEXT_SELECTOR = 'input, textarea, [contenteditable="true"]';

function easeCalm(t: number): number {
  const inv = 1 - t;
  return 1 - inv * inv * inv;
}

export function LanternCursor() {
  const enabled = useCursorEnabled();
  const pathname = usePathname();
  const isAdmin = pathname?.startsWith('/admin') ?? false;
  // Pond pages own the cursor experience: the WebGL scene injects water
  // ripples at the pointer and the koi react, so the custom cursor is
  // suppressed there and the native cursor shows instead. Inner pages keep
  // the paper-lantern cursor (no pond to replace it).
  const isPondPage = pathname === '/' || (pathname?.startsWith('/pond-v3') ?? false);
  const suppressed = isAdmin || isPondPage;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!enabled || suppressed) return;
    document.documentElement.dataset.cursor = 'on';

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    let targetX = width / 2;
    let targetY = height / 2;
    let dotX = targetX;
    let dotY = targetY;
    let ringX = targetX;
    let ringY = targetY;
    let lastRippleX = dotX;
    let lastRippleY = dotY;
    let lastMoveMs = -Infinity;
    let visible = false;
    let hoverState: 'default' | 'hover' | 'text' = 'default';
    let transitioning = false;
    let transitionTimerId: ReturnType<typeof setTimeout> | null = null;
    const onSceneTransition = () => {
      if (transitionTimerId) clearTimeout(transitionTimerId);
      // 800ms cross-fade total: cursor desat covers the middle 400ms
      // (200ms in, then desat for 400ms) so the cream tone hits at the
      // perceptual midpoint instead of pulling on entry.
      transitionTimerId = setTimeout(() => {
        transitioning = true;
        transitionTimerId = setTimeout(() => {
          transitioning = false;
        }, 400);
      }, 200);
    };
    window.addEventListener('scene:transition', onSceneTransition);

    const onMove = (e: PointerEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
      visible = true;
      lastMoveMs = performance.now();
      const target = e.target as Element | null;
      if (target?.closest?.(TEXT_SELECTOR)) hoverState = 'text';
      else if (target?.closest?.(HOVERABLE_SELECTOR)) hoverState = 'hover';
      else hoverState = 'default';
    };
    const onLeave = () => {
      visible = false;
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);

    const ripples: Ripple[] = [];

    let rafId = 0;
    const tick = (nowMs: number) => {
      const onSand = document.documentElement.dataset.surface === 'sand';
      dotX += (targetX - dotX) * DOT_LERP;
      dotY += (targetY - dotY) * DOT_LERP;
      ringX += (targetX - ringX) * RING_LERP;
      ringY += (targetY - ringY) * RING_LERP;

      ctx.clearRect(0, 0, width, height);

      // Ripple gate: only seed a new ring when the cursor has actually
      // *landed* somewhere — either after a long move (≥44px since last
      // ripple) or after a brief pause and re-engagement (>150ms idle then
      // a new move). The brush trail carries "I'm moving"; ripples carry
      // "I'm landing." Skipped inside text fields.
      if (visible && hoverState !== 'text') {
        const dx = dotX - lastRippleX;
        const dy = dotY - lastRippleY;
        const distanceQualifies = Math.hypot(dx, dy) >= RIPPLE_MIN_DISTANCE;
        const idleQualifies =
          nowMs - lastMoveMs > RIPPLE_IDLE_MS &&
          Math.hypot(dx, dy) > 6; // tiny re-engage threshold
        if (distanceQualifies || idleQualifies) {
          if (ripples.length >= RIPPLE_MAX) ripples.shift();
          ripples.push({ x: dotX, y: dotY, startMs: nowMs });
          window.dispatchEvent(
            new CustomEvent('lantern:ripple', { detail: { x: dotX, y: dotY } }),
          );
          lastRippleX = dotX;
          lastRippleY = dotY;
        }
      }

      // Ripple draw — under the cursor
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i];
        const age = (nowMs - r.startMs) / RIPPLE_LIFE_MS;
        if (age >= 1) {
          ripples.splice(i, 1);
          continue;
        }
        const eased = easeCalm(age);
        const radius = eased * RIPPLE_MAX_RADIUS;
        const alpha = (1 - age) * 0.45;
        ctx.beginPath();
        ctx.strokeStyle = onSand
          ? `rgba(74, 58, 42, ${alpha.toFixed(3)})`
          : `rgba(212, 165, 116, ${alpha.toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
        ctx.stroke();
      }

      if (visible) {
        if (hoverState === 'text') {
          // Tight vertical I-beam — height matches a typical 16px input
          // line height; serif caps dropped to stop the "floating element"
          // read flagged in the Wave D audit.
          const h = 18;
          const w = 1.2;
          ctx.fillStyle = onSand
            ? 'rgba(74, 58, 42, 0.85)'
            : 'rgba(248, 230, 194, 0.85)';
          ctx.fillRect(ringX - w / 2, ringY - h / 2, w, h);
        } else {
          const ringR = hoverState === 'hover' ? RING_RADIUS_HOVER : RING_RADIUS_DEFAULT;
          const baseAlpha = hoverState === 'hover' ? 0.35 : 0.55;
          const ringAlpha = onSand ? Math.min(0.85, baseAlpha + 0.2) : baseAlpha;
          const ringColor = transitioning
            ? `rgba(232, 220, 196, ${ringAlpha.toFixed(3)})`
            : onSand
              ? `rgba(74, 58, 42, ${ringAlpha.toFixed(3)})`
              : `rgba(212, 165, 116, ${ringAlpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.strokeStyle = ringColor;
          ctx.lineWidth = 1.1;
          ctx.arc(ringX, ringY, ringR, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Sharp paper-lantern dot, always
        ctx.beginPath();
        ctx.fillStyle = onSand
          ? 'rgba(74, 58, 42, 0.95)'
          : 'rgba(248, 230, 194, 0.95)';
        ctx.arc(dotX, dotY, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      if (transitionTimerId) clearTimeout(transitionTimerId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('scene:transition', onSceneTransition);
      document.removeEventListener('pointerleave', onLeave);
      delete document.documentElement.dataset.cursor;
    };
  }, [enabled, suppressed]);

  if (!enabled || suppressed) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        // Cursor sits above ALL UI by design — a pointing indicator
        // must never be obscured by toasts/modals/floating widgets.
        // The 2026-05-24 audit flagged 9999 as a code-smell; it is in
        // fact deliberate. pointer-events:none means the cursor never
        // intercepts clicks, so the high z-index has zero interaction
        // cost. CommandPalette (admin, z-9000) doesn't coexist with
        // this cursor because the cursor is suppressed on /admin via
        // useCursorEnabled + the isAdmin pathname check above.
        zIndex: 9999,
      }}
    />
  );
}
